import React from "react";
import { ArrowLeftRight, Star, Calendar, User } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";

const ExchangeCard = ({ exchange, currentUserId, onOpenModal }) => {
  // Determine if current user is initiator or recipient
  const isInitiator = exchange.initiator._id === currentUserId;
  const otherUser = isInitiator ? exchange.recipient : exchange.initiator;
  const userListing = isInitiator
    ? exchange.initiatorListing
    : exchange.recipientListing;
  const otherListing = isInitiator
    ? exchange.recipientListing
    : exchange.initiatorListing;
  const hasUserRated = isInitiator
    ? exchange.initiatorRated
    : exchange.recipientRated;

  // Get the rating the current user gave to the other person
  const userGaveRating = isInitiator
    ? exchange.initiatorRating
    : exchange.recipientRating;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleRateClick = () => {
    // Create a modified exchange object with userToRate property
    const exchangeWithUserToRate = {
      ...exchange,
      userToRate: otherUser
    };
    onOpenModal(exchangeWithUserToRate);
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={14}
            className={
              star <= rating ? "text-yellow-400 fill-current" : "text-gray-300"
            }
          />
        ))}
      </div>
    );
  };

  return (
    <Card className="overflow-hidden hover:shadow-lg transition-shadow">
      <CardContent className="!p-6">
        {/* Exchange Header */}
        <div className="flex items-center gap-3 mb-4">
          <Calendar size={16} className="text-sage-600" />
          <span className="text-sm text-muted-foreground">
            Completed on {formatDate(exchange.completedAt)}
          </span>
        </div>

        {/* Listings Exchange Visual */}
        <div className="flex items-center gap-4 mb-4">
          {/* User's Listing */}
          <div className="flex-1">
            <div className="aspect-square rounded-lg overflow-hidden bg-gray-100 mb-2">
              {userListing.image ? (
                <img
                  src={userListing.image}
                  alt={userListing.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400">
                  No Image
                </div>
              )}
            </div>
            <p className="text-sm font-medium text-center line-clamp-2">
              {userListing.title}
            </p>
            <p className="text-xs text-muted-foreground text-center">
              Your item
            </p>
          </div>

          {/* Exchange Arrow */}
          <div className="flex-shrink-0 p-2">
            <ArrowLeftRight className="text-sage-600" size={24} />
          </div>

          {/* Other User's Listing */}
          <div className="flex-1">
            <div className="aspect-square rounded-lg overflow-hidden bg-gray-100 mb-2">
              {otherListing.image ? (
                <img
                  src={otherListing.image}
                  alt={otherListing.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400">
                  No Image
                </div>
              )}
            </div>
            <p className="text-sm font-medium text-center line-clamp-2">
              {otherListing.title}
            </p>
            <p className="text-xs text-muted-foreground text-center">
              Their item
            </p>
          </div>
        </div>

        {/* Other User Info */}
        <div className="flex items-center gap-3 mb-4 p-3 bg-sage-50 rounded-lg">
          <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
            {otherUser.image ? (
              <img
                src={otherUser.image}
                alt={`${otherUser.firstName} ${otherUser.lastName}`}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <User size={16} className="text-gray-400" />
              </div>
            )}
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium">
              Exchanged with {otherUser.firstName} {otherUser.lastName}
            </p>
            <div className="flex items-center gap-1">
              <Star size={12} className="text-yellow-400 fill-current" />
              <span className="text-xs text-muted-foreground">
                {otherUser.rating ? otherUser.rating.toFixed(1) : "No rating"}
              </span>
            </div>
          </div>
        </div>

        {/* Rating Section */}
        {!hasUserRated ? (
          <div className="border-t pt-4">
            <p className="text-sm font-medium mb-3">
              Rate your experience with {otherUser.firstName}
            </p>
            <Button
              onClick={handleRateClick}
              className="w-full bg-sage-600 hover:bg-sage-700 text-white"
            >
              Rate Exchange
            </Button>
          </div>
        ) : (
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-muted-foreground">
                You rated {otherUser.firstName}:
              </p>
              {renderStars(userGaveRating)}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ExchangeCard;